import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PawPrint, Play, SkipForward } from "@phosphor-icons/react";

// 🎙️ Ryan's recorded "Imagine" manifesto voiceover.
const VOICEOVER_URL =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_wiggles-unified/artifacts/d6ibp98j_Imagine%20%281%29.m4a";

// 🎬 Cinematic "Heaven Ranch Film" footage — video plays INSIDE the letters.
const V = "https://07970fe6-31d3-43dd-8766-0cbf790c75c8.preview.emergentagent.com/videos/";
const HEAVEN_MP4 = V + "heaven.mp4";
const UNBROKEN_MP4 = V + "unbroken.mp4";
const HERO_MP4 = V + "hero.mp4";
const P = "https://static.prod-images.emergentagent.com/jobs/a3627e1a-1bc0-4c36-880b-13d1b55f3a36/images/";
const HEAVEN_POSTER = P + "a41c83112badc3367fff5c7dc009a96019428f0324527b573e03c441e908af0a.jpeg";
const UNBROKEN_POSTER = P + "a801eabef799dc5e19c8103371dfbaa324cf963530ef2a8634e7f6b6adab4d4e.jpeg";
const HERO_POSTER = P + "2638bbfce4648274bbb38c73ac7c9d31038e49c0e1237629d9f49c1aebe725e4.jpeg";

// Video knocked out of a full-screen dark overlay so footage shows only inside the word.
function TextMask({ word, src, poster, sub }) {
  const id = "mask-" + word;
  const fs = word.length > 6 ? 150 : 190;
  return (
    <div className="absolute inset-0">
      <video src={src} poster={poster} autoPlay muted loop playsInline preload="auto"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ filter: "saturate(1.25) contrast(1.05)" }} />
      {/* magenta→cyan duotone wash */}
      <div className="absolute inset-0 mix-blend-color pointer-events-none"
        style={{ background: "linear-gradient(120deg, rgba(255,43,214,0.55), rgba(0,229,255,0.55))" }} />
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1000 600" preserveAspectRatio="xMidYMid slice" aria-hidden>
        <defs>
          <mask id={id}>
            <rect width="1000" height="600" fill="white" />
            <text x="500" y="330" textAnchor="middle" fill="black"
              fontFamily="'Space Grotesk', Impact, sans-serif" fontWeight="800"
              fontSize={fs} letterSpacing="4">{word}</text>
          </mask>
        </defs>
        <rect width="1000" height="600" fill="#060010" mask={`url(#${id})`} />
      </svg>
      {sub && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 1 }}
          className="absolute inset-x-0 bottom-[18%] text-center overline text-white/70 tracking-[0.3em] text-xs sm:text-sm">
          {sub}
        </motion.p>
      )}
    </div>
  );
}

export default function SplashScreen() {
  const [show, setShow] = useState(() => sessionStorage.getItem("wiggles_splash") !== "done");
  const [stage, setStage] = useState("gate"); // gate | title | heaven | unbroken | hero
  const voiceRef = useRef(null);
  const dismissed = useRef(false);
  const timers = useRef([]);

  const clearTimers = () => { timers.current.forEach((t) => clearTimeout(t)); timers.current = []; };

  const dismiss = () => {
    if (dismissed.current) return;
    dismissed.current = true;
    clearTimers();
    try { voiceRef.current && voiceRef.current.pause(); } catch (e) { console.debug("voiceover pause failed", e); }
    sessionStorage.setItem("wiggles_splash", "done");
    setShow(false);
  };

  const enter = () => {
    setStage("title");
    const v = voiceRef.current;
    if (VOICEOVER_URL && v) { v.volume = 1; v.play().catch((e) => console.debug("voiceover autoplay blocked", e)); }
    // Cinematic timeline (~19s total), fully skippable.
    timers.current.push(setTimeout(() => setStage("heaven"), 3200));
    timers.current.push(setTimeout(() => setStage("unbroken"), 9400));
    timers.current.push(setTimeout(() => setStage("hero"), 15600));
    timers.current.push(setTimeout(dismiss, 19400));
  };

  // Fade the voiceover out over its final 3s.
  const onTimeUpdate = () => {
    const v = voiceRef.current;
    if (!v || !v.duration) return;
    const remaining = v.duration - v.currentTime;
    if (remaining <= 3) v.volume = Math.max(0, remaining / 3);
  };

  useEffect(() => () => clearTimers(), []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, filter: "blur(10px)" }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          className="fixed inset-0 z-[100] bg-[#060010] overflow-hidden grid place-items-center"
          data-testid="splash-screen"
        >
          {stage === "gate" && (
            <video src={HERO_MP4} poster={HERO_POSTER} autoPlay muted loop playsInline
              className="absolute inset-0 h-full w-full object-cover opacity-30" />
          )}
          <AnimatePresence mode="wait">
            {stage === "gate" && (
              <motion.div key="gate" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }} className="relative text-center px-6">
                <div className="inline-flex items-center gap-2 mb-6 opacity-90">
                  <PawPrint weight="fill" className="text-primary" size={26} />
                  <span className="font-modern text-xl font-semibold tracking-tight">MR<span className="text-primary">.</span>WIGGLEZ</span>
                </div>
                <p className="overline text-white/50 mb-4">A Heaven Ranch Film</p>
                <button onClick={enter} data-testid="splash-enter-btn"
                  className="group relative inline-flex items-center gap-3 px-9 py-4 rounded-[10px] border-2 border-primary text-primary font-modern font-semibold uppercase tracking-[0.2em] text-sm neon-glow hover:bg-primary hover:text-primary-foreground transition-colors pulse-ring">
                  <Play weight="fill" size={16} /> Enter with sound
                </button>
                <div className="mt-6">
                  <button onClick={dismiss} data-testid="splash-skip-gate"
                    className="overline text-white/40 hover:text-white/80 transition-colors inline-flex items-center gap-1.5">
                    Skip intro <SkipForward size={13} />
                  </button>
                </div>
              </motion.div>
            )}

            {stage === "title" && (
              <motion.div key="title" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.8 }} className="relative text-center px-6">
                <p className="overline text-white/50 tracking-[0.4em] mb-4">Mr. Wigglez presents</p>
                <h2 className="font-display font-black uppercase text-4xl sm:text-6xl tracking-tight"
                  style={{ background: "linear-gradient(120deg,#ff2bd6,#00e5ff)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  A Heaven Ranch Film
                </h2>
              </motion.div>
            )}

            {stage === "heaven" && (
              <motion.div key="heaven" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1 }} className="absolute inset-0">
                <TextMask word="HEAVEN" src={HEAVEN_MP4} poster={HEAVEN_POSTER} sub="Where the pack runs free" />
              </motion.div>
            )}

            {stage === "unbroken" && (
              <motion.div key="unbroken" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1 }} className="absolute inset-0">
                <TextMask word="UNBROKEN" src={UNBROKEN_MP4} poster={UNBROKEN_POSTER} sub="They stood when the world sat down" />
              </motion.div>
            )}

            {stage === "hero" && (
              <motion.div key="hero" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1 }} className="absolute inset-0">
                <video src={HERO_MP4} poster={HERO_POSTER} autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30" />
                <div className="absolute inset-0 grid place-items-center text-center px-6">
                  <div>
                    <h2 className="font-display font-black uppercase text-3xl sm:text-5xl leading-tight">
                      Let&apos;s bring <span className="neon-text">&apos;em home</span>.
                    </h2>
                    <p className="overline text-white/50 mt-3">— Ryan · Mr. Wigglez</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {stage !== "gate" && (
            <button onClick={dismiss} data-testid="splash-skip-playing"
              className="absolute bottom-8 right-8 z-10 overline text-white/60 hover:text-white transition-colors inline-flex items-center gap-1.5 bg-black/40 backdrop-blur px-4 py-2 rounded-full border border-white/10">
              Enter the shop <SkipForward size={13} />
            </button>
          )}

          <audio ref={voiceRef} src={VOICEOVER_URL || undefined} onTimeUpdate={onTimeUpdate} preload="auto" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
